import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import lifecycleService from '../../../services/lifecycleService';
import clientSiteService from '../../../services/clientSiteService';
import jobApplicationService from '../../../services/jobApplicationService';

export default function HireApplicantModal({
  applicantId,
  offerId,
  applicantName,
  requireDeployment = false,
  isOpen,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  const [applicant, setApplicant] = useState(null);
  const [clientSites, setClientSites] = useState([]);
  const [clientSiteId, setClientSiteId] = useState('');
  const [createLogin, setCreateLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  useEffect(() => {
    if (!isOpen || !applicantId) return;

    const load = async () => {
      setFetching(true);
      setError(null);
      const [appResult, sitesResult] = await Promise.all([
        jobApplicationService.getApplication(applicantId),
        clientSiteService.getClientSites({ is_active: 'true' }),
      ]);

      if (appResult.success) {
        const data = appResult.data;
        setApplicant(data);
        setLoginEmail(data.email || '');
      } else {
        setError(appResult.error || 'Could not load applicant');
      }

      if (sitesResult.success) {
        setClientSites(sitesResult.data || []);
      }

      setFetching(false);
    };
    load();
  }, [isOpen, applicantId]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setClientSiteId('');
      setCreateLogin(false);
      setLoginPassword('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requireDeployment && !clientSiteId) {
      setError('Please select a client site for deployment');
      return;
    }
    if (createLogin && (!loginEmail || loginPassword.length < 6)) {
      setError('Login email and password (min 6 chars) are required');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      applicant_id: applicantId,
      offer_letter_id: offerId || undefined,
      require_deployment: requireDeployment,
      client_site_id: clientSiteId || undefined,
      create_login: createLogin,
      login_email: createLogin ? loginEmail : undefined,
      login_password: createLogin ? loginPassword : undefined,
    };

    const result = await lifecycleService.hireApplicant(payload);
    setLoading(false);

    if (result.success) {
      onSuccess?.(result.data);
      onClose();
    } else {
      setError(result.error || result.message || 'Hire failed');
    }
  };

  if (!isOpen) return null;

  const displayName = applicant?.applicant_name || applicantName || 'Applicant';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {requireDeployment ? 'Hire & Deploy' : 'Unified Hire'}
            </h2>
            <p className="text-sm text-gray-500">{displayName}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {fetching && <p className="text-sm text-gray-500">Loading applicant details…</p>}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
          )}

          <div className="text-sm text-gray-600 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
            Creates employee record, onboarding checklist, and updates applicant status.
            {requireDeployment && ' Also assigns client deployment and marks applicant as deployed.'}
          </div>

          {(requireDeployment || clientSites.length > 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Site {requireDeployment && <span className="text-red-500">*</span>}
              </label>
              <select
                value={clientSiteId}
                onChange={(e) => setClientSiteId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                required={requireDeployment}
              >
                <option value="">Select client site…</option>
                {clientSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.site_name}
                    {site.company_name ? ` — ${site.company_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={createLogin}
              onChange={(e) => setCreateLogin(e.target.checked)}
              className="rounded border-gray-300"
            />
            Create employee portal login
          </label>

          {createLogin && (
            <div className="space-y-3 pl-6 border-l-2 border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Login email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  minLength={6}
                  required
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={fetching} iconName="UserPlus">
              {requireDeployment ? 'Hire & Deploy' : 'Complete Hire'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
